import React, { useMemo } from 'react'
import { Tag, TreeSelect } from 'antd'
import { isEmpty } from 'lodash'

const { SHOW_CHILD } = TreeSelect

/**
 * ImpactType — รองรับ 2 โหมด:
 *  1. (ใหม่) รับ `data` เป็น flat array จาก GET_IMPACTTYPE
 *     พร้อม `selectedNameMap` ({ "id": "name" }) สำหรับ inactive items
 *  2. (เดิม) รับ `masterData` แบบ nested tree (ใช้ใน AdditionalBusiness)
 */
const ImpactType = (props) => {
  const {
    // props ใหม่
    data,
    selectedNameMap,
    // props เดิม (backward-compatible)
    masterData,
    choiceImpactTypeInactive,
    // shared props
    setChoiceImpactType,
    choiceCompany,
    choiceImpactType,
    disabled,
  } = props

  let url_page = window.location.pathname

  const isInfoPage =
    !url_page.includes('/management/powerbi') &&
    !url_page.includes('/create-project') &&
    !url_page.includes('/information')

  // ─── โหมดใหม่: flat data จาก API ───────────────────────────────────────
  const flatTreeData = useMemo(() => {
    if (!data) return null

    const activeIds = new Set(data.map((item) => String(item.id)))

    const activeOptions = data.map((item) => ({
      title: item.name_en || item.name,
      value: String(item.id),
      key: String(item.id),
      disabled: false,
    }))

    // inactive items — แสดงใน dropdown เฉพาะ view mode (disabled), edit mode ซ่อนออก
    const inactiveOptions = isInfoPage
      ? (choiceImpactType || [])
          .filter((id) => !activeIds.has(String(id)))
          .map((id) => ({
            title: selectedNameMap?.[String(id)] || String(id),
            value: String(id),
            key: String(id),
            disabled: true,
          }))
      : []

    return [...activeOptions, ...inactiveOptions]
  }, [data, choiceImpactType, selectedNameMap, isInfoPage])

  // ─── โหมดเดิม: nested tree (backward-compatible) ──────────────────────
  const legacyTreeData = useMemo(() => {
    if (!masterData) return []

    const items = []
    masterData.forEach((parent, idxParent) => {
      parent.children?.forEach((child, idxChild) => {
        items.push({
          ...child,
          key: `0-${idxParent}-${idxChild}`,
          disabled: false,
          children: null,
        })
      })
    })

    // inactive items จาก choiceImpactTypeInactive
    const activeIds = new Set(items.map((c) => String(c.value)))
    const inactiveItems = (choiceImpactTypeInactive || [])
      .filter((item) => !activeIds.has(String(item.id)))
      .map((item, index) => ({
        title: item.name_en || item.name_th || String(item.id),
        value: String(item.id),
        key: `inactive-${index}`,
        disabled: true,
      }))

    return [...items, ...inactiveItems]
  }, [masterData, choiceImpactTypeInactive])

  const treeData = flatTreeData !== null ? flatTreeData : legacyTreeData

  function onChange(value) {
    setChoiceImpactType(value)
  }

  const tProps = {
    treeData,
    value: choiceImpactType,
    onChange,
    treeCheckable: true,
    showCheckedStrategy: SHOW_CHILD,
    placeholder: 'Select Stage',
    style: { width: '100%' },
  }

  if (isInfoPage) {
    return (
      <TreeSelect
        disabled={isEmpty(choiceCompany)}
        size="large"
        treeDefaultExpandAll={true}
        {...tProps}
        maxTagCount={0}
        maxTagPlaceholder={(event) => event[0]?.label}
        tagRender={(event) => {
          const { label } = event
          return (
            <Tag
              style={{
                display: 'flex',
                fontSize: 12,
                borderRadius: '16px',
                background: 'rgba(7, 78, 159, 0.1)',
                height: '32px',
                alignItems: 'center',
                color: '#074E9F',
              }}
            >
              {choiceImpactType?.length > 1 ? 'Multi select stage' : label}
            </Tag>
          )
        }}
      />
    )
  }

  // หน้า edit — inactive items ไม่อยู่ใน dropdown แต่แสดงเป็น tag ปกติผ่าน labelInValue
  const labeledValue = (choiceImpactType || []).map((id) => ({
    label:
      treeData.find((item) => item.value === id)?.title ??
      (selectedNameMap?.[id] || id),
    value: id,
  }))

  return (
    <TreeSelect
      size="large"
      treeDefaultExpandAll={true}
      {...tProps}
      labelInValue={true}
      value={labeledValue}
      onChange={(values) => setChoiceImpactType(values.map((v) => v.value))}
      disabled={disabled}
    />
  )
}

export default ImpactType
